import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { History, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Loader2, Table as TableIcon, Hash, TrendingUp, Calendar, Pencil } from "lucide-react";
import { toast } from "sonner";
import { listRecipeRuns, getRun } from "@/lib/api";
import { formatAbsolute, formatRelative } from "@/lib/format";
import CapturesEvolutionChart from "./CapturesEvolutionChart";

const SOURCE_LABEL = {
  manual: "Manual",
  schedule: "Agendada",
  range: "Intervalo (multi-dia)",
  interval: "Intervalo (1 run)",
  days: "Por dia",
  dashboard: "Dashboard",
};

function toIsoDate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayIso() { return toIsoDate(new Date()); }

export default function RunsHistoryDialog({ recipe }) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Filter mode: "date" (specific day) or "all"
  const [filterMode, setFilterMode] = useState("date");
  const [filterDate, setFilterDate] = useState(todayIso());

  // Available dates extracted from runs (for quick navigation)
  const availableDates = useMemo(() => {
    const set = new Set();
    runs.forEach((r) => {
      if (r.started_at) {
        try { set.add(toIsoDate(new Date(r.started_at))); } catch { /* noop */ }
      }
    });
    return Array.from(set).sort().reverse(); // newest first
  }, [runs]);

  // Filter the runs list based on filter mode
  const filteredRuns = useMemo(() => {
    if (filterMode === "all") return runs;
    // date mode
    return runs.filter((r) => {
      if (!r.started_at) return false;
      try { return toIsoDate(new Date(r.started_at)) === filterDate; } catch { return false; }
    });
  }, [runs, filterMode, filterDate]);

  async function refresh() {
    setLoading(true);
    try {
      const data = await listRecipeRuns(recipe.id);
      setRuns(data || []);
    } catch (e) {
      toast.error(`Falha a carregar capturas: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-select the most recent run in the current filtered view
  useEffect(() => {
    if (filteredRuns.length === 0) {
      setSelectedId(null);
      return;
    }
    // If current selection is not in the filtered list, switch to the most recent
    const stillVisible = selectedId && filteredRuns.some((r) => r.id === selectedId);
    if (!stillVisible) {
      setSelectedId(filteredRuns[0].id);
    }
  }, [filteredRuns, selectedId]);

  useEffect(() => {
    if (!selectedId) { setSelectedRun(null); return; }
    setLoadingDetail(true);
    getRun(selectedId)
      .then(setSelectedRun)
      .catch((e) => toast.error(`Falha ao abrir captura: ${e?.response?.data?.detail || e.message}`))
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="recipe-card-captures-button" title="Ver dados recolhidos em capturas anteriores">
          <History className="h-3.5 w-3.5 mr-1"/> Capturas
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4 text-primary"/>
            Capturas anteriores
            <span className="text-sm font-normal text-muted-foreground">— {recipe.name}</span>
          </DialogTitle>
          <DialogDescription>
            Lista das últimas execuções (até 500). Clica numa linha à esquerda para veres exatamente que dados o sistema recolheu nessa captura.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end justify-between gap-3 -mt-2">
          {/* Day picker - shared by both tabs */}
          <div className="flex flex-wrap items-center gap-2" data-testid="captures-day-picker">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" /> Dia:
            </div>
            <input
              type="date"
              value={filterDate}
              max={todayIso()}
              onChange={(e) => { setFilterDate(e.target.value); setFilterMode("date"); }}
              data-testid="captures-date-input"
              className="bg-card border border-border rounded-md px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={filterMode === "all"}
            />
            <button
              type="button"
              onClick={() => { setFilterDate(todayIso()); setFilterMode("date"); }}
              data-testid="captures-day-today"
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                filterMode === "date" && filterDate === todayIso()
                  ? "bg-emerald-600 text-white border-transparent"
                  : "bg-card border-border hover:bg-muted text-foreground"
              }`}
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={() => setFilterMode(filterMode === "all" ? "date" : "all")}
              data-testid="captures-day-all"
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                filterMode === "all"
                  ? "bg-emerald-600 text-white border-transparent"
                  : "bg-card border-border hover:bg-muted text-foreground"
              }`}
              title="Mostrar todas as capturas (todos os dias)"
            >
              Tudo
            </button>
            {availableDates.length > 0 && filterMode === "date" && (
              <div className="hidden sm:flex items-center gap-1 ml-2">
                <span className="text-[11px] text-muted-foreground">Dias c/ dados:</span>
                {availableDates.slice(0, 5).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setFilterDate(d)}
                    className={`text-[11px] px-1.5 py-0.5 rounded border transition-colors ${
                      filterDate === d
                        ? "bg-sky-600 text-white border-transparent"
                        : "bg-card border-border hover:bg-muted text-muted-foreground"
                    }`}
                    title={d}
                  >
                    {d.slice(8, 10)}/{d.slice(5, 7)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="ghost" onClick={refresh} disabled={loading} data-testid="captures-refresh-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`}/> Atualizar
          </Button>
        </div>

        <Tabs defaultValue="table" className="w-full">
          <TabsList className="sticky top-0 z-10 grid grid-cols-2 w-full sm:w-auto sm:inline-flex">
            <TabsTrigger value="table" data-testid="captures-tab-table">
              <TableIcon className="h-3.5 w-3.5 mr-1.5" /> Tabela
            </TabsTrigger>
            <TabsTrigger value="evolution" data-testid="captures-tab-evolution">
              <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Evolução
            </TabsTrigger>
          </TabsList>

          <TabsContent value="evolution" className="mt-3">
            {filteredRuns.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground flex items-start gap-3" data-testid="captures-evolution-no-day">
                <Calendar className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  {filterMode === "all"
                    ? "Não existem capturas guardadas."
                    : <>Não há recolhas no dia <strong>{filterDate}</strong>. {availableDates.length > 0 && <>Tenta um dos dias com dados em cima.</>}</>
                  }
                </div>
              </div>
            ) : (
              <>
                {loadingDetail && !selectedRun && (
                  <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin"/> A carregar dados da captura…
                  </div>
                )}
                {(!loadingDetail || selectedRun) && (
                  <CapturesEvolutionChart run={selectedRun} />
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="table" className="mt-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 h-[70vh] items-stretch">
          {/* Left: list of runs */}
          <div className="md:col-span-4 border border-border rounded-lg overflow-hidden bg-card flex flex-col min-h-0">
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border bg-muted/40 shrink-0">
              {filteredRuns.length} execuç{filteredRuns.length === 1 ? 'ão' : 'ões'} {filterMode === "all" ? "(todos os dias)" : `em ${filterDate}`}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
              {loading && filteredRuns.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin"/> A carregar…
                </div>
              )}
              {!loading && runs.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">
                  Ainda não há capturas. Carrega em "Correr" pelo menos uma vez.
                </div>
              )}
              {!loading && runs.length > 0 && filteredRuns.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">
                  Não há capturas em <strong>{filterDate}</strong>.
                  {availableDates.length > 0 && (
                    <span className="block mt-1 text-[11px]">
                      Dias com dados: {availableDates.slice(0, 5).join(", ")}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setFilterMode("all")}
                    className="mt-2 underline text-primary hover:no-underline text-xs"
                  >
                    Ver todas as capturas
                  </button>
                </div>
              )}
              {filteredRuns.map((r) => {
                const isOk = r.status === 'success';
                const isPartial = r.status === 'partial';
                const isSelected = selectedId === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    data-testid={`capture-row-${r.id}`}
                    className={`w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/10' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs">
                        {isOk ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600"/> :
                          isPartial ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600"/> :
                          <AlertTriangle className="h-3.5 w-3.5 text-destructive"/>}
                        <span className="font-medium">{formatAbsolute(r.started_at)}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 capitalize">{SOURCE_LABEL[r.source] || r.source || '—'}</Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><TableIcon className="h-3 w-3"/> {(r.columns || []).length} col</span>
                      <span className="flex items-center gap-1"><Hash className="h-3 w-3"/> {r.row_count ?? r.rows_count ?? '—'} linhas</span>
                      <span>{formatRelative(r.started_at)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: details of selected run */}
          <div className="md:col-span-8 border border-border rounded-lg overflow-hidden bg-card flex flex-col min-h-0">
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border bg-muted/40 flex items-center justify-between shrink-0">
              <span>Dados recolhidos</span>
              {selectedRun && (
                <Button asChild size="sm" variant="ghost" className="h-7 text-xs" data-testid="capture-open-full-button">
                  <Link to={`/runs/${selectedRun.id}`}>
                    Abrir captura completa <ExternalLink className="h-3 w-3 ml-1"/>
                  </Link>
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {!selectedId && (
                <div className="p-6 text-sm text-muted-foreground">
                  Seleciona uma captura à esquerda para veres os dados.
                </div>
              )}
              {loadingDetail && (
                <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin"/> A carregar dados…
                </div>
              )}
              {selectedRun && !loadingDetail && (
                <RunDetailView run={selectedRun}/>
              )}
            </div>
          </div>
        </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function RunDetailView({ run }) {
  const cols = run.columns || [];
  const rows = run.rows || [];
  const runDate = (() => {
    try { return run.started_at ? toIsoDate(new Date(run.started_at)) : null; } catch { return null; }
  })();
  return (
    <div className="p-3 space-y-3">
      <Card className="p-3 bg-muted/30">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Metric label="Início" value={formatAbsolute(run.started_at)}/>
          <Metric label="Fim" value={formatAbsolute(run.finished_at)}/>
          <Metric label="Estado" value={({ success: "sucesso", partial: "parcial", error: "erro", running: "a correr" })[run.status] || run.status} highlight={run.status}/>
          <Metric label="Origem" value={SOURCE_LABEL[run.source] || run.source || '—'}/>
          <Metric label="Linhas" value={rows.length}/>
          <Metric label="Colunas" value={cols.length}/>
          <Metric label="URL final" value={run.url || '—'} truncate/>
          {run.error && <Metric label="Erro" value={run.error} highlight="error" truncate/>}
        </div>
      </Card>

      {runDate && rows.length > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 px-3 py-2">
          <div className="text-xs text-amber-900 dark:text-amber-200 flex items-center gap-2 min-w-0">
            <Pencil className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Precisas de corrigir um valor capturado? Justifica linhas individuais (Real vs Sugerido) na página de Anomalias.</span>
          </div>
          <Link
            to={`/anomalias?date=${runDate}`}
            data-testid="runs-history-go-justify"
            className="shrink-0 text-xs px-2.5 py-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 font-medium inline-flex items-center gap-1"
          >
            Justificar linhas <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}

      {cols.length === 0 ? (
        <div className="text-sm text-muted-foreground p-3">Esta captura não devolveu colunas.</div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/40 border-b border-border flex items-center justify-between">
            <span>Pré-visualização — {rows.length} linha{rows.length === 1 ? '' : 's'} × {cols.length} coluna{cols.length === 1 ? '' : 's'}</span>
            <span className="text-[10px] opacity-70">scroll vertical/horizontal para ver tudo</span>
          </div>
          <div className="overflow-auto max-h-[55vh]" data-testid="capture-preview-scroll">
            <table className="w-full text-xs border-separate border-spacing-0">
              <thead className="sticky top-0 z-10">
                <tr>
                  {cols.map((c, i) => (
                    <th
                      key={i}
                      className="text-left px-2 py-2 font-semibold text-foreground bg-muted border-b-2 border-border whitespace-pre-wrap align-bottom"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="even:bg-muted/30 hover:bg-primary/5">
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="px-2 py-1.5 border-b border-border/50 whitespace-pre-wrap align-top bg-card"
                      >
                        {String(cell ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, highlight, truncate }) {
  let cls = "text-foreground font-medium";
  if (highlight === 'success') cls = "text-emerald-600 font-semibold";
  else if (highlight === 'partial') cls = "text-amber-600 font-semibold";
  else if (highlight === 'error') cls = "text-destructive font-semibold";
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-0.5 ${cls} ${truncate ? 'truncate' : ''}`} title={truncate ? String(value || '') : undefined}>
        {String(value ?? '—')}
      </div>
    </div>
  );
}
